import type { Icon, IconProps } from '@phosphor-icons/react'
import { forwardRef, type ReactNode } from 'react'
import { cn } from './primitives'

/*
 * One coloured SVG mark per Operations / Pick Up screen (the legacy VSoft Dispatch screens). Each is drawn on a
 * 24-unit grid with its own hue so the colour says which screen it is before the words do, and each component is
 * a Phosphor-compatible icon (size / className / aria props) so it drops into PageHeader, PAGE_ICONS and the sidebar.
 */

const TONE = {
  green: '#16a34a', amber: '#d97706', blue: '#2563eb', red: '#dc2626', violet: '#7c3aed', teal: '#0d9488', pink: '#db2777', indigo: '#4f46e5', orange: '#ea580c', sky: '#0284c7', lime: '#65a30d', slate: '#475569',
}

function mark(name: string, fg: string, draw: (fg: string) => ReactNode): Icon {
  const C = forwardRef<SVGSVGElement, IconProps>(function OpsIcon({ size = 24, className, weight: _w, mirrored, color: _c, alt, ...rest }, ref) {
    return (
      <svg ref={ref} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden={alt ? undefined : true} aria-label={alt} focusable={false} className={cn('keep-color shrink-0', className)} style={mirrored ? { transform: 'scale(-1, 1)' } : undefined} {...rest}>
        {draw(fg)}
      </svg>
    )
  })
  C.displayName = name
  return C as unknown as Icon
}

const box = (fg: string) => (
  <>
    <path d="M12 2.8 20 6.8v10.4l-8 4-8-4V6.8l8-4Z" fill={fg} opacity=".16" />
    <path d="M12 2.8 20 6.8v10.4l-8 4-8-4V6.8l8-4Z" stroke={fg} strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M4.4 7 12 10.8 19.6 7M12 10.8V21" stroke={fg} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </>
)
const badge = (fg: string, cx = 17.5, cy = 17.5) => <circle cx={cx} cy={cy} r="4.8" fill={fg} />

/** Create Shipment — a box with a plus badge. */
export const CreateShipmentIcon = mark('CreateShipmentIcon', TONE.green, (fg) => (
  <>
    {box(fg)}
    {badge(fg)}
    <path d="M17.5 15.2v4.6M15.2 17.5h4.6" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
  </>
))

/** Edit Shipment — a box with a pencil badge. */
export const EditShipmentIcon = mark('EditShipmentIcon', TONE.amber, (fg) => (
  <>
    {box(fg)}
    {badge(fg)}
    <path d="m15.6 19.4.5-1.9 2.8-2.8 1.4 1.4-2.8 2.8-1.9.5Z" fill="#fff" />
  </>
))

/** Upload Excel — a spreadsheet with an upward arrow. */
export const UploadExcelIcon = mark('UploadExcelIcon', TONE.lime, (fg) => (
  <>
    <rect x="3.5" y="3" width="13" height="18" rx="2" fill={fg} opacity=".16" stroke={fg} strokeWidth="1.8" />
    <path d="M6.5 8h7M6.5 11.5h7M6.5 15h4" stroke={fg} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M10 7.8v0" stroke={fg} />
    {badge(fg, 18, 17)}
    <path d="M18 19.6v-5M15.9 16.6 18 14.5l2.1 2.1" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </>
))

/** Receive Shipment — a barcode being scanned into a tray. */
export const ReceiveShipmentIcon = mark('ReceiveShipmentIcon', TONE.blue, (fg) => (
  <>
    <path d="M3 13.5V18a2.5 2.5 0 0 0 2.5 2.5h13A2.5 2.5 0 0 0 21 18v-4.5" stroke={fg} strokeWidth="1.8" strokeLinecap="round" />
    <path d="M3 13.5h5l1.5 2.5h5l1.5-2.5h5" fill={fg} opacity=".18" stroke={fg} strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M7 3.5v6M10 3.5v6M12.5 3.5v6M15.5 3.5v6M17 3.5v6" stroke={fg} strokeWidth="1.6" strokeLinecap="round" />
    <path d="M5 8.5h14" stroke={fg} strokeWidth="1.1" opacity=".55" />
  </>
))

/** Manage Transactions — a ledger with an undo arrow. */
export const ManageTransactionsIcon = mark('ManageTransactionsIcon', TONE.indigo, (fg) => (
  <>
    <rect x="3" y="3.5" width="15" height="17" rx="2.2" fill={fg} opacity=".14" stroke={fg} strokeWidth="1.8" />
    <path d="M6.5 8.5h8M6.5 12h8M6.5 15.5h4.5" stroke={fg} strokeWidth="1.5" strokeLinecap="round" />
    {badge(fg, 17.5, 17.5)}
    <path d="M15.4 17.3h3.2a1.4 1.4 0 0 1 0 2.8h-.6" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
    <path d="m16.6 15.9-1.4 1.4 1.4 1.4" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </>
))

/** Manage AWBs — a barcode label with a wrench badge. */
export const ManageAwbsIcon = mark('ManageAwbsIcon', TONE.violet, (fg) => (
  <>
    <rect x="2.5" y="5" width="19" height="13" rx="2.2" fill={fg} opacity=".14" stroke={fg} strokeWidth="1.8" />
    <path d="M6 8.5v6M8.5 8.5v6M11 8.5v6M13.5 8.5v6M16 8.5v6M18 8.5v6" stroke={fg} strokeWidth="1.5" strokeLinecap="round" />
    {badge(fg, 18, 18)}
    <path d="m15.8 20.2 2.6-2.6m0 0a1.4 1.4 0 1 0 1.5-1.5l-.9.9-.6-.6.9-.9a1.4 1.4 0 0 0-1.5 1.5l.6.6Z" stroke="#fff" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
  </>
))

/** Reoperate Shipments — a box inside a refresh ring. */
export const ReoperateIcon = mark('ReoperateIcon', TONE.teal, (fg) => (
  <>
    <path d="M12 6.2 16.5 8.5v4.9L12 15.7l-4.5-2.3V8.5L12 6.2Z" fill={fg} opacity=".2" stroke={fg} strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M7.7 8.7 12 10.9l4.3-2.2M12 10.9v4.6" stroke={fg} strokeWidth="1.3" strokeLinecap="round" />
    <path d="M20.5 12a8.5 8.5 0 0 1-14.9 5.6" stroke={fg} strokeWidth="1.9" strokeLinecap="round" />
    <path d="M3.5 12a8.5 8.5 0 0 1 14.9-5.6" stroke={fg} strokeWidth="1.9" strokeLinecap="round" />
    <path d="m18.2 3.4.4 3.3-3.3.3M5.8 20.6l-.4-3.3 3.3-.3" stroke={fg} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </>
))

/** Runners Returns — a courier's box coming back on a U-turn. */
export const RunnersReturnsIcon = mark('RunnersReturnsIcon', TONE.red, (fg) => (
  <>
    <rect x="3" y="10" width="11" height="10" rx="2" fill={fg} opacity=".18" stroke={fg} strokeWidth="1.8" />
    <path d="M3.6 13.6h9.8" stroke={fg} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M8 4.8h8.2a4.3 4.3 0 0 1 0 8.6H14" stroke={fg} strokeWidth="2" strokeLinecap="round" />
    <path d="m10.6 2.2-2.8 2.6 2.8 2.6" stroke={fg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="19" cy="18.5" r="2.4" fill={fg} />
    <path d="M16.6 18.5h-1.4" stroke={fg} strokeWidth="1.8" strokeLinecap="round" />
  </>
))

/** POD Review — a photo of the handover with a check badge. */
export const PodReviewIcon = mark('PodReviewIcon', TONE.sky, (fg) => (
  <>
    <rect x="3" y="4" width="18" height="14" rx="2.2" fill={fg} opacity=".14" stroke={fg} strokeWidth="1.8" />
    <path d="m4.5 16.5 4.6-5.2a1 1 0 0 1 1.5 0l3 3.4 1.7-1.8a1 1 0 0 1 1.5 0l2.7 3" stroke={fg} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="8.5" cy="8.5" r="1.6" fill={fg} />
    {badge(fg, 18.5, 18)}
    <path d="m16.5 18 1.4 1.4 2.6-2.8" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </>
))

/** Edit Weight — a scale with a box on it. */
export const EditWeightIcon = mark('EditWeightIcon', TONE.orange, (fg) => (
  <>
    <path d="M6 9.5h12l2 10.5H4L6 9.5Z" fill={fg} opacity=".16" stroke={fg} strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M9 9.5 12 3.5l3 6" stroke={fg} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="3.6" r="1.4" fill={fg} />
    <path d="M9.5 14.5h5M12 13v3.5" stroke={fg} strokeWidth="1.6" strokeLinecap="round" />
  </>
))

/** New Pickup — a hand receiving a box, with a plus. */
export const NewPickupIcon = mark('NewPickupIcon', TONE.green, (fg) => (
  <>
    <rect x="8" y="3" width="9" height="8" rx="1.6" fill={fg} opacity=".2" stroke={fg} strokeWidth="1.6" />
    <path d="M12.5 5v4M10.5 7h4" stroke={fg} strokeWidth="1.6" strokeLinecap="round" />
    <path d="M3 14.5h3.2l3.3 1.4h4.6a1.4 1.4 0 0 1 0 2.8H9.6M14 17.6l4.2-1.6a1.6 1.6 0 0 1 2 .9v0a1.6 1.6 0 0 1-.8 2L13.2 21H9.5L6.2 19.6H3" stroke={fg} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </>
))

/** Assign Pickup To Runner — a runner with an arrow onto the route. */
export const AssignPickupIcon = mark('AssignPickupIcon', TONE.pink, (fg) => (
  <>
    <circle cx="6.5" cy="17" r="3" fill={fg} opacity=".2" stroke={fg} strokeWidth="1.6" />
    <circle cx="18" cy="17" r="3" fill={fg} opacity=".2" stroke={fg} strokeWidth="1.6" />
    <path d="M6.5 17 10 10h4l4 7M10 10 8.4 6.5H6" stroke={fg} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="15.6" cy="4.8" r="2" fill={fg} />
    <path d="M12.2 13.5h6.6" stroke={fg} strokeWidth="1.4" strokeLinecap="round" opacity=".7" />
  </>
))

/** Receive Pickup — a box landing in an open tray with a check. */
export const ReceivePickupIcon = mark('ReceivePickupIcon', TONE.blue, (fg) => (
  <>
    <path d="M12 3v8M8.8 7.8 12 11l3.2-3.2" stroke={fg} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 13v5.5A2.5 2.5 0 0 0 5.5 21h13a2.5 2.5 0 0 0 2.5-2.5V13" stroke={fg} strokeWidth="1.8" strokeLinecap="round" />
    <path d="M3 13h5l1.5 2.5h5L16 13h5" fill={fg} opacity=".18" stroke={fg} strokeWidth="1.6" strokeLinejoin="round" />
  </>
))

/** Pickup Inquiry — a magnifier over a list of pickups. */
export const PickupInquiryIcon = mark('PickupInquiryIcon', TONE.slate, (fg) => (
  <>
    <rect x="3" y="4" width="14" height="16" rx="2.2" fill={fg} opacity=".12" stroke={fg} strokeWidth="1.8" />
    <path d="M6.5 8.5h7M6.5 12h5M6.5 15.5h4" stroke={fg} strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="16.5" cy="15" r="3.6" fill="#fff" stroke={fg} strokeWidth="1.8" />
    <path d="m19.2 17.7 2.3 2.3" stroke={fg} strokeWidth="2" strokeLinecap="round" />
  </>
))

/** Every screen mark by page id (navigation + page headers). */
export const OPS_ICONS = {
  'operations.create': CreateShipmentIcon,
  'operations.edit': EditShipmentIcon,
  'operations.upload': UploadExcelIcon,
  'operations.receive': ReceiveShipmentIcon,
  'operations.transactions': ManageTransactionsIcon,
  'operations.awbs': ManageAwbsIcon,
  'operations.reoperate': ReoperateIcon,
  'operations.runnerReturns': RunnersReturnsIcon,
  'operations.pod': PodReviewIcon,
  'operations.weight': EditWeightIcon,
  'pickup.new': NewPickupIcon,
  'pickup.assign': AssignPickupIcon,
  'pickup.receive': ReceivePickupIcon,
  'pickup.inquiry': PickupInquiryIcon,
} as const satisfies Record<string, Icon>
